import React, { useEffect, useState } from 'react';
import PortalV2Layout from './PortalV2Layout';
import { useAuth } from '../contexts/AuthContext';
import { Building2, Plus, X, Trash2, AlertCircle, CheckCircle, RefreshCw } from 'lucide-react';

const AdminV2Domains = () => {
  const { authFetch } = useAuth();
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [newDomain, setNewDomain] = useState({ domain: '', company_name: '', visibility_mode: 'individual', notes: '' });
  const [msg, setMsg] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await authFetch('/api/admin/domains');
      if (res.ok) {
        const d = await res.json();
        setDomains(d.domains || d || []);
      } else setError('Failed to load domains');
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const addDomain = async () => {
    setActionLoading('add');
    try {
      const res = await authFetch('/api/admin/approve/domain', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newDomain),
      });
      if (res.ok) {
        setShowAdd(false);
        setNewDomain({ domain: '', company_name: '', visibility_mode: 'individual', notes: '' });
        setMsg({ type: 'green', text: 'Domain approved.' });
        await load();
      } else setMsg({ type: 'red', text: 'Could not approve domain.' });
    } finally { setActionLoading(null); setTimeout(() => setMsg(null), 4000); }
  };

  const updateMode = async (domain, mode) => {
    setActionLoading(domain);
    try {
      await authFetch('/api/admin/domain/visibility', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain, visibility_mode: mode }),
      });
      await load();
    } finally { setActionLoading(null); }
  };

  const removeDomain = async (domain) => {
    if (!window.confirm(`Remove ${domain}?`)) return;
    setActionLoading(domain);
    try {
      await authFetch(`/api/admin/domain/${encodeURIComponent(domain)}`, { method: 'DELETE' });
      await load();
    } finally { setActionLoading(null); }
  };

  return (
    <PortalV2Layout
      eyebrow="Access Control"
      title="Approved Domains"
      subtitle="Email domains permitted to register portal accounts."
      crumbs={<><span style={{ opacity: 0.5 }}>Admin /</span> <strong style={{ marginLeft: 6 }}>Domains</strong></>}
      action={
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={load} disabled={loading} className="pv2-btn pv2-btn-ghost" data-testid="pv2-refresh-domains">
            <RefreshCw size={14} strokeWidth={1.5} className={loading ? 'pv2-skeleton' : ''} /> Refresh
          </button>
          <button onClick={() => setShowAdd(true)} className="pv2-btn pv2-btn-primary" data-testid="pv2-add-domain"><Plus size={14} /> Add Domain</button>
        </div>
      }
    >
      {error && <div className="pv2-alert pv2-alert-red"><AlertCircle size={14} /><span>{error}</span></div>}
      {msg && <div className={`pv2-alert pv2-alert-${msg.type}`}>{msg.type === 'green' ? <CheckCircle size={14} /> : <AlertCircle size={14} />}<span>{msg.text}</span></div>}

      {/* Helper card - How Domain Approval Works (above table) */}
      <div className="pv2-card" style={{ marginBottom: 24, padding: 20 }}>
        <div className="pv2-mono-tag" style={{ marginBottom: 10 }}>How Domain Approval Works</div>
        <ul style={{ fontSize: 13, color: 'var(--pv2-text-muted)', lineHeight: 1.8, listStyle: 'none', padding: 0, margin: 0 }}>
          <li>• <strong style={{ color: 'var(--pv2-text)' }}>Individual Mode:</strong> Users from this domain only see their own jobs</li>
          <li>• <strong style={{ color: 'var(--pv2-text)' }}>Shared Mode:</strong> All users from this domain can see all jobs for the domain</li>
          <li>• When a domain is pre-approved, new users from that domain are automatically approved</li>
        </ul>
      </div>

      <div className="pv2-table-wrap">
        <table className="pv2-table" data-testid="pv2-admin-domains-table">
          <thead><tr><th>Domain</th><th>Company</th><th>Visibility Mode</th><th>Approved</th><th></th></tr></thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="pv2-table-empty">Loading…</td></tr>}
            {!loading && domains.length === 0 && <tr><td colSpan={5} className="pv2-table-empty"><Building2 size={20} style={{ opacity: 0.3, marginBottom: 8 }} />No domains yet.</td></tr>}
            {!loading && domains.map(d => (
              <tr key={d.domain}>
                <td className="pv2-table-job-num">{d.domain}</td>
                <td>{d.company_name || d.firm_name || '—'}</td>
                <td>
                  <select
                    className="pv2-select"
                    value={d.visibility_mode || 'individual'}
                    onChange={(e) => updateMode(d.domain, e.target.value)}
                    disabled={actionLoading === d.domain}
                    style={{ padding: '6px 10px', fontSize: 12, width: 'auto', minWidth: 140 }}
                    data-testid={`pv2-domain-mode-${d.domain}`}
                  >
                    <option value="individual">Individual</option>
                    <option value="shared">Shared</option>
                  </select>
                </td>
                <td className="pv2-mono" style={{ textTransform: 'none', letterSpacing: 0, fontSize: 11 }}>
                  {d.approved_at ? new Date(d.approved_at).toLocaleDateString() : '—'}
                </td>
                <td>
                  <button onClick={() => removeDomain(d.domain)} disabled={actionLoading === d.domain} title="Remove domain" style={{ background: 'transparent', border: 0, color: 'var(--pv2-red-bright)', cursor: 'pointer' }}><Trash2 size={13} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={() => setShowAdd(false)}>
          <div style={{ width: '100%', maxWidth: 460, background: 'var(--pv2-ink)', border: '1px solid var(--pv2-ink-line)', padding: 28 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 className="pv2-display" style={{ fontSize: 22 }}>Approve domain</h3>
              <button onClick={() => setShowAdd(false)} style={{ background: 'transparent', border: 0, color: 'var(--pv2-text-muted)', cursor: 'pointer' }}><X size={18} /></button>
            </div>
            <div className="pv2-field">
              <label className="pv2-label">Email domain</label>
              <input className="pv2-input" placeholder="lawfirm.com" value={newDomain.domain} onChange={(e) => setNewDomain(p => ({ ...p, domain: e.target.value }))} data-testid="pv2-new-domain" />
            </div>
            <div className="pv2-field">
              <label className="pv2-label">Company / Firm name</label>
              <input className="pv2-input" placeholder="Smith & Associates" value={newDomain.company_name} onChange={(e) => setNewDomain(p => ({ ...p, company_name: e.target.value }))} data-testid="pv2-new-firm" />
            </div>
            <div className="pv2-field">
              <label className="pv2-label">Visibility mode</label>
              <select className="pv2-select" value={newDomain.visibility_mode} onChange={(e) => setNewDomain(p => ({ ...p, visibility_mode: e.target.value }))}>
                <option value="individual">Individual — users see only their own jobs</option>
                <option value="shared">Shared — all users from this domain see all jobs</option>
              </select>
            </div>
            <div className="pv2-field">
              <label className="pv2-label">Notes <span style={{ opacity: 0.5 }}>(optional)</span></label>
              <textarea className="pv2-textarea" rows={3} value={newDomain.notes} onChange={(e) => setNewDomain(p => ({ ...p, notes: e.target.value }))} />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowAdd(false)} className="pv2-btn pv2-btn-ghost">Cancel</button>
              <button onClick={addDomain} disabled={actionLoading === 'add' || !newDomain.domain} className="pv2-btn pv2-btn-primary" data-testid="pv2-approve-domain-btn">{actionLoading === 'add' ? 'Approving…' : 'Approve'}</button>
            </div>
          </div>
        </div>
      )}
    </PortalV2Layout>
  );
};

export default AdminV2Domains;
